import { createContext, useCallback, useEffect, useState } from 'react';
import { apiUrl, authHeaders } from './shell';
import { onAgentsChanged } from '@/workspace/agents/api';
import {
  fetchStarterResult,
  starterPromptKind,
  starterSuggestions,
  type StarterPause,
  type StarterPrompt,
  type StarterPromptKind,
} from './starter-prompts';

export const StarterSuggestionsContext = createContext<{
  prompts: StarterPrompt[];
  loading: boolean;
  /** Set while the company's billing pauses new suggestions; the last cards stay. */
  pause?: StarterPause | null;
  refresh: () => void;
}>({ prompts: [], loading: false, pause: null, refresh: () => {} });

/** The starting points under the composer, for the conversation being started.
 * Creating a new Skill asks for Skills worth adding; ordinary chat keeps the
 * company's starter questions. Each kind is requested the first time it is
 * shown and then kept, so no model work happens on a page load that never
 * enters Skill authoring, and returning to chat needs no second wait. */
export function useStarterSuggestions({
  threadId,
  locale,
  intent,
  scope = '',
  enabled = true,
}: {
  threadId: string;
  locale: string;
  intent?: { mode?: string; skillId?: string };
  scope?: string;
  enabled?: boolean;
}) {
  const kind = starterPromptKind(intent);
  const owner = `${scope}:${locale}`;
  const [state, setState] = useState<{
    owner: string;
    prompts: Partial<Record<StarterPromptKind, StarterPrompt[]>>;
    pause: Partial<Record<StarterPromptKind, StarterPause | null>>;
  }>({ owner, prompts: {}, pause: {} });
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const refresh = useCallback(() => setAttempt((value) => value + 1), []);
  // Home suggestions are a stored set the server refreshes once a day on a visit; only Skill suggestions follow
  // agent changes, a return to the tab and a five-minute interval.
  const followsChanges = enabled && kind === 'skill';
  useEffect(
    () => (followsChanges ? onAgentsChanged(refresh) : undefined),
    [followsChanges, refresh],
  );
  useEffect(() => {
    if (!enabled) return;
    let active = true;
    const abort = new AbortController();
    setLoading(true);
    const read = (waitForRefresh: boolean) => {
      const query = new URLSearchParams({ thread_id: threadId, locale, kind });
      if (waitForRefresh) query.set('wait_for_refresh', '1');
      void fetchStarterResult({
        endpoint: apiUrl(`/api/v1/starter-prompts?${query}`),
        headers: authHeaders(),
        signal: abort.signal,
      }).then(({ prompts, refreshing, pause }) => {
        if (!active) return;
        // A failed read changes nothing; a billing refusal sets the pause and keeps the shown cards.
        if (prompts || pause)
          setState((current) => {
            const same = current.owner === owner;
            const shown = same ? current.prompts : {};
            return {
              owner,
              prompts: prompts ? { ...shown, [kind]: prompts } : shown,
              pause: { ...(same ? current.pause : {}), [kind]: pause },
            };
          });
        // A refresh already running is joined once, and its new set replaces these cards when it is ready.
        const follow = Boolean(prompts) && refreshing && !waitForRefresh;
        setLoading(follow);
        if (follow) read(true);
      });
    };
    read(false);
    return () => {
      active = false;
      abort.abort();
    };
  }, [threadId, locale, kind, owner, enabled, attempt]);
  useEffect(() => {
    if (!followsChanges) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(onVisible, 5 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [followsChanges, refresh]);
  const prompts = enabled && state.owner === owner ? (state.prompts[kind] ?? []) : [];
  const pause = enabled && state.owner === owner ? (state.pause[kind] ?? null) : null;
  return {
    kind,
    prompts,
    loading: enabled && loading,
    pause,
    refresh,
    suggestions: starterSuggestions(prompts),
    // Refresh only the suggestion resource; the thread/composer stays mounted.
    suggestionKey: `${kind}:${prompts.map(({ id }) => id).join(':') || 'loading'}`,
  };
}
